/**
 * FormPickerComponent
 * Search and select a Form by ID with external edit button and full-screen modal
 */

import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLookupDropdown } from '@atlas-forms/ui-components-react';
import { FormBuilderCanvas } from '@atlas-forms/designer-components-react';
import { FormEditor } from '@atlas-forms/form-editor-react';
import { AtlasFormsClient } from '@atlas-forms/client-js';
import type { FormDefinition, FormSchema, RenderOptions } from '@atlas-forms/types-js';
import type { ControlRendererProps } from '../ControlRenderer';
import './FormPickerComponent.css';

/**
 * FormPickerConfig - Configuration for Form Picker control
 * Stored in FormControl.config and exposed through property panel
 * All properties are optional with sensible defaults
 */
export interface FormPickerConfig {
  // General visibility
  showEditButton?: boolean;
  showClearButton?: boolean;
  showSearchButton?: boolean;

  // Editor rendering options
  editorContainerSelector?: string;
  editorDefaultTab?: 'designer' | 'json' | 'settings';
  editorHideDesignerTab?: boolean;
  editorHideJsonTab?: boolean;
  editorHideSettingsTab?: boolean;
  editorHideNavigationTabs?: boolean;
  editorHidePropertiesPanel?: boolean;
  editorHideFieldToolbox?: boolean;
  editorHideSaveButton?: boolean;
}

/**
 * Default Form Picker configuration
 * All buttons visible, all editor UI visible, modal rendering
 */
export const DEFAULT_FORM_PICKER_CONFIG: FormPickerConfig = {
  showEditButton: true,
  showClearButton: true,
  showSearchButton: true,
  editorContainerSelector: undefined,
  editorDefaultTab: 'designer',
  editorHideDesignerTab: false,
  editorHideJsonTab: false,
  editorHideSettingsTab: false,
  editorHideNavigationTabs: false,
  editorHidePropertiesPanel: false,
  editorHideFieldToolbox: false,
  editorHideSaveButton: false,
};

interface FormPickerComponentProps extends Omit<ControlRendererProps, 'onChange' | 'onBlur'> {
  onChange?: (formID: number | undefined) => void;
  onBlur?: () => void;
}

// isNodeForm: false — same class of bug fixed in App Studio's own (separately duplicated)
// fetchForms copies: every ExecutionNode's design-time config panel is itself stored as a Form
// row, so an unfiltered search here would surface those instead of real, end-user-facing Forms
// this picker control is meant to find.
async function fetchForms(search?: string, page?: number): Promise<FormDefinition[]> {
  const client = AtlasFormsClient.getInstance();
  const res = await client.listForms({ search, page, pageSize: 20, isNodeForm: false });
  return res.success && res.data ? res.data.items : [];
}

async function loadForm(formID: number): Promise<FormDefinition | undefined> {
  return AtlasFormsClient.getInstance().getFormDefinition(formID);
}

// SVG Icons
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 2H14V6M14 2L6 10M6 2H2V14H14V10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Build RenderOptions from FormPickerConfig
 * Maps config properties to RenderOptions for FormEditor
 */
function buildRenderOptions(config: FormPickerConfig): RenderOptions {
  return {
    containerSelector: config.editorContainerSelector,
    defaultTab: config.editorDefaultTab ?? 'designer',
    hideDesignerTab: config.editorHideDesignerTab ?? false,
    hideJsonTab: config.editorHideJsonTab ?? false,
    hideSettingsTab: config.editorHideSettingsTab ?? false,
    hideNavigationTabs: config.editorHideNavigationTabs ?? false,
    hidePropertiesPanel: config.editorHidePropertiesPanel ?? false,
    hideFieldToolbox: config.editorHideFieldToolbox ?? false,
    hideSaveButton: config.editorHideSaveButton ?? false,
  };
}

export const FormPickerComponent: React.FC<FormPickerComponentProps> = ({
  control,
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const label = control.label;
  const required = control.required ?? false;
  const helpText = control.helpText;

  const [selectedFormId, setSelectedFormId] = useState<number | undefined>(typeof value === 'number' ? value : undefined);
  const [selectedFormData, setSelectedFormData] = useState<FormDefinition | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [useFormEditor, setUseFormEditor] = useState(false);

  // Extract config from control.config with defaults
  const config = useMemo(
    () => ({ ...DEFAULT_FORM_PICKER_CONFIG, ...(control?.config as Partial<FormPickerConfig> ?? {}) }),
    [control?.config],
  );

  // Build renderOptions from config
  const renderOptions = useMemo(() => buildRenderOptions(config), [config]);

  const inputId = useMemo(() => `form-picker-${label?.replace(/\s+/g, '-').toLowerCase()}`, [label]);

  // Lookup dropdown hook for search-as-you-type
  const { open, query, results, loading, containerRef, inputRef, closeDropdown, handleQueryChange } =
    useLookupDropdown(fetchForms, 250);

  // Auto-open dropdown when user types (search-as-you-type behavior)
  const shouldShowDropdown = query.length > 0;

  // Prevent parent form submission when modal is open
  useEffect(() => {
    if (!isModalOpen) return;

    const handleFormSubmit = (e: SubmitEvent) => {
      const target = e.target as HTMLElement;
      if (document.querySelector('.fpc-modal-overlay')) {
        // Modal is open - prevent form submission
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('submit', handleFormSubmit, true);
    return () => document.removeEventListener('submit', handleFormSubmit, true);
  }, [isModalOpen]);

  // Format form display label
  const formatFormLabel = (form: FormDefinition): string => {
    const id = form.FormID ?? (form as any).FormId ?? (form as any).formID;
    const name = form.Name ?? (form as any).name ?? '';
    return name ? `${id} — ${name}` : String(id);
  };

  const handleFormSelected = (form: FormDefinition) => {
    const formId = form.FormID ?? (form as any).FormId ?? (form as any).formID;
    if (!formId) return;

    setSelectedFormId(formId);
    setSelectedLabel(formatFormLabel(form));
    onChange?.(formId);
    closeDropdown();
    handleQueryChange(''); // Clear query to close dropdown
  };

  const handleEditClick = async () => {
    if (!selectedFormId) return;

    // If containerSelector is configured, use FormEditor for embedded rendering
    // Otherwise use modal rendering with FormEditor
    if (renderOptions.containerSelector) {
      setUseFormEditor(true);
    } else {
      setIsLoading(true);
      try {
        const form = await loadForm(selectedFormId);
        if (form) {
          console.log('Form loaded successfully:', { formId: selectedFormId, form });
          setSelectedFormData(form);
          setIsModalOpen(true);
        } else {
          console.error('Form not found for ID:', selectedFormId);
        }
      } catch (error) {
        console.error('Failed to load form:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setUseFormEditor(false);
  };

  const handleSaveForm = async (schema: FormSchema) => {
    if (selectedFormId && selectedFormData) {
      try {
        console.log('Saving form schema:', { formId: selectedFormId, schema });

        // Update the selectedFormData with new schema
        const updatedForm = {
          ...selectedFormData,
          Schema: typeof schema === 'string' ? schema : JSON.stringify(schema),
        };

        await AtlasFormsClient.getInstance().updateFormDefinition(selectedFormId, updatedForm);
        console.log('Form saved successfully');

        // Update the state with the new schema
        setSelectedFormData(updatedForm);
      } catch (error) {
        console.error('Failed to save form:', error);
      }
    }
  };

  // Embedded rendering mode: render FormEditor directly if containerSelector provided
  const embeddedEditorContent = useFormEditor && selectedFormId && renderOptions.containerSelector && (
    <FormEditor
      formId={selectedFormId}
      renderOptions={renderOptions}
      onClose={handleCloseModal}
      onSave={handleSaveForm}
    />
  );

  const modalContent = isModalOpen && selectedFormData && !renderOptions.containerSelector && (
    <div className="fpc-modal-overlay" onClick={handleCloseModal}>
      <div className="fpc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fpc-modal__header">
          <div className="fpc-modal__header-content">
            <div className="fpc-modal__header-meta">
              <span className="fpc-modal__form-id">ID: {selectedFormId}</span>
              <span className="fpc-modal__form-name">{selectedFormData?.Name || `Form #${selectedFormId}`}</span>
            </div>
          </div>
          <div className="fpc-modal__header-actions">
            <button className="fpc-btn fpc-btn--secondary fpc-btn-small" onClick={handleCloseModal} type="button">
              Cancel
            </button>
            <button className="fpc-btn fpc-btn--primary fpc-btn-small" onClick={handleCloseModal} type="button">
              Done
            </button>
            <button className="fpc-modal__close" onClick={handleCloseModal} aria-label="Close" type="button">
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="fpc-modal__body" onClick={(e) => e.stopPropagation()}>
          {isLoading ? (
            <div className="fpc-modal__loading">
              <div className="fpc-spinner"></div>
              <p>Loading form editor...</p>
            </div>
          ) : (() => {
            // Parse schema - it might be a string or object
            let parsedSchema = null;
            if (selectedFormData?.Schema) {
              try {
                parsedSchema = typeof selectedFormData.Schema === 'string'
                  ? JSON.parse(selectedFormData.Schema)
                  : selectedFormData.Schema;
                console.log('Parsed schema:', parsedSchema);
              } catch (e) {
                console.error('Failed to parse schema:', e);
              }
            }

            return parsedSchema ? (
              <FormBuilderCanvas
                initialSchema={parsedSchema}
                onSchemaChange={handleSaveForm}
                hideDesignerTab={renderOptions.hideDesignerTab}
                hideJsonTab={renderOptions.hideJsonTab}
                hideSettingsTab={renderOptions.hideSettingsTab}
                hideNavigationTabs={renderOptions.hideNavigationTabs}
                hidePropertiesPanel={renderOptions.hidePropertiesPanel}
                hideFieldToolbox={renderOptions.hideFieldToolbox}
                hideSaveButton={renderOptions.hideSaveButton}
                defaultTab={renderOptions.defaultTab}
              />
            ) : (
              <div className="fpc-modal__error">
                <p>No schema found for this form</p>
                <details>
                  <summary>Debug Info</summary>
                  <pre>{JSON.stringify(selectedFormData, null, 2)}</pre>
                </details>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );

  // Ensure FormEditor portal container exists with high z-index
  // This ensures FormEditor modal appears ABOVE nested modals like ConnectorConfigDialog
  const ensureFormEditorPortal = () => {
    let portal = document.getElementById('fpc-form-editor-portal');
    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'fpc-form-editor-portal';
      portal.style.position = 'fixed';
      portal.style.top = '0';
      portal.style.left = '0';
      portal.style.width = '100%';
      portal.style.height = '100%';
      portal.style.pointerEvents = 'none';
      portal.style.zIndex = '9999999'; // Higher than ModalPortal (999999)
      document.body.appendChild(portal);
    }
    return portal;
  };

  return (
    <>
      <div className="af-form-field__group form-picker-group">
        {label && (
          <label className="af-form-field__label" htmlFor={inputId}>
            {label}
            {required && <span className="af-form-field__required">*</span>}
          </label>
        )}

        <div className="fpc-wrapper" ref={containerRef}>
          {/* Search Input - Direct, no click needed */}
          <div className="fpc-search-container">
            {config.showSearchButton !== false && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  opacity: 0.5,
                }}
              >
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
            <input
              ref={inputRef}
              type="text"
              className="fpc-search-input"
              value={selectedLabel || query}
              onChange={(e) => {
                if (config.showSearchButton !== false) {
                  setSelectedLabel('');
                  handleQueryChange(e.target.value);
                }
              }}
              onFocus={() => {
                // Auto-open dropdown on focus if there's a selection or query
                if ((selectedLabel || query) && config.showSearchButton !== false) {
                  handleQueryChange(query);
                }
              }}
              placeholder={config.showSearchButton !== false ? "Search forms…" : "Search disabled"}
              disabled={disabled || config.showSearchButton === false}
              aria-label="Search forms"
              style={config.showSearchButton !== false ? {} : { paddingLeft: '12px' }}
            />

            {/* Clear Button Inside Input - Conditionally shown based on config */}
            {(selectedLabel || query) && config.showClearButton !== false && (
              <button
                className="fpc-search-clear"
                onClick={() => {
                  handleQueryChange('');
                  setSelectedLabel('');
                  setSelectedFormId(undefined);
                  onChange?.(undefined);
                  inputRef.current?.focus();
                }}
                title="Clear search"
                aria-label="Clear search"
                type="button"
              >
                ✕
              </button>
            )}

            {/* Dropdown Results - Shows when typing */}
            {shouldShowDropdown && (
              <div className="fpc-dropdown">
                {loading && <div className="fpc-dropdown__state">Loading…</div>}
                {!loading && results.length === 0 && <div className="fpc-dropdown__state">No forms found</div>}
                {!loading &&
                  results.map((form) => {
                    const formId = form.FormID ?? (form as any).FormId ?? (form as any).formID;
                    if (!formId) return null;
                    return (
                      <div
                        key={formId}
                        className={`fpc-dropdown__item ${formId === selectedFormId ? 'fpc-dropdown__item--selected' : ''}`}
                        onClick={() => handleFormSelected(form)}
                      >
                        <span className="fpc-dropdown__item-id">{formId}</span>
                        <span className="fpc-dropdown__item-name">{form.Name ?? (form as any).name}</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Edit Button - Conditionally shown based on config */}
          {selectedFormId && config.showEditButton !== false && (
            <button
              className="fpc-btn-edit"
              onClick={handleEditClick}
              disabled={disabled || isLoading}
              title="Edit form"
              aria-label="Edit form"
              type="button"
            >
              <EditIcon />
            </button>
          )}
        </div>

        {error && (
          <div id={`${inputId}-error`} className="af-form-field__error">
            {error}
          </div>
        )}

        {helpText && (
          <div id={`${inputId}-help`} className="af-form-field__help">
            {helpText}
          </div>
        )}
      </div>

      {/* Embedded rendering: FormEditor directly into containerSelector target */}
      {embeddedEditorContent}

      {/* Modal rendering: Use high z-index portal to render above nested modals like ConnectorConfigDialog */}
      {typeof document !== 'undefined' && createPortal(modalContent, ensureFormEditorPortal())}
    </>
  );
};

FormPickerComponent.displayName = 'FormPickerComponent';
